import type { ReactNode } from "react";
import {
    AimOutlined,
    CalculatorOutlined,
    CompassOutlined,
    DollarOutlined,
    EnvironmentOutlined,
    LockOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    ToolOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";

// Guides catalogue. Single source of truth — imported by GuidesIndex
// (the full /guides listing) and Landing (the first four shown on the
// home page). Add new entries here and keep sitemap.xml in sync.
//
// Each entry should set a topical AntD icon and an accentColor from
// --gt-feature-* as a fallback for the card visual; once an `image` is
// supplied, the photo takes precedence and the icon is unused.

export type Guide = {
    slug: string;
    title: string;
    summary: string;
    readingTime: string;
    icon: ReactNode;
    accentColor: string;
    image?: { src: string; alt?: string };
    // ISO date — used for Schema.org Article datePublished and the
    // sitemap.xml lastmod. Keep in sync with the sitemap entry.
    datePublished: string;
    // ISO date of the most recent meaningful edit. Falls back to
    // datePublished in the schema builder when omitted. Bump this when
    // you make an editorial change worth signalling freshness for.
    dateModified?: string;
};

export const GUIDES: Guide[] = [
    {
        slug: "packing-europe",
        title:
            "What to pack for a motorbike tour in France, Germany, Switzerland and Italy",
        summary:
            "A rider's checklist for European touring: documents, country-specific legal kit, riding gear, tools, navigation, comfort, money, medical, and the day-before checks.",
        readingTime: "8 min read",
        icon: <UnorderedListOutlined />,
        accentColor: "var(--gt-feature-checklist)",
        image: {
            src: "/assets/mc_packing.jpg",
            alt: "Motorbike loaded with panniers and a tank bag ready for a tour",
        },
        datePublished: "2026-06-06",
        dateModified: "2026-06-29",
    },
    {
        slug: "cycle-touring-packing",
        title:
            "What to pack for a cycle tour through France, Germany, Switzerland and Italy",
        summary:
            "A cyclist's checklist for cross-border European touring: documents, country-specific cycling rules, kit, the bike itself, tools, navigation, comfort, and the day-before checks.",
        readingTime: "7 min read",
        icon: <CompassOutlined />,
        accentColor: "var(--gt-feature-expense)",
        image: {
            src: "/assets/cycle_packing.jpg",
            alt: "Loaded touring bicycle with panniers ready for a cross-border ride",
        },
        datePublished: "2026-06-11",
        dateModified: "2026-06-29",
    },
    {
        slug: "power-plugs",
        title:
            "Power plugs around the world — voltages, pin layouts and adapters",
        summary:
            "A practical reference to the plug types, voltages and frequencies you'll meet on a multi-country tour — plus what adapters and chargers to actually pack.",
        readingTime: "7 min read",
        icon: <ThunderboltOutlined />,
        accentColor: "var(--gt-feature-currency)",
        image: {
            src: "/assets/plug-hero.jpg",
            alt: "Universal travel power adapter charging a phone and tablet via multiple USB cables",
        },
        datePublished: "2026-06-22",
        dateModified: "2026-06-29",
    },
    {
        slug: "french-toll-roads",
        title: "How to use French toll roads on a motorbike",
        summary:
            "How the péage system works, which lane to use, Liber-t electronic tags, the new Flux Libre barrier-less rollout, group ride logistics, and how to keep costs sensible.",
        readingTime: "6 min read",
        icon: <EnvironmentOutlined />,
        accentColor: "var(--gt-feature-itinerary)",
        image: {
            src: "/assets/french_tolls.jpg",
            alt: "French autoroute péage toll plaza",
        },
        datePublished: "2026-06-06",
        dateModified: "2026-08-29",
    },
    {
        slug: "bike-security",
        title: "Keeping your motorbike from being stolen — at home and on tour",
        summary:
            "How bike theft actually happens, what works at home, what to carry on tour, locks and trackers worth the money, insurance small print, and what to do if your bike is taken.",
        readingTime: "9 min read",
        icon: <LockOutlined />,
        accentColor: "var(--gt-feature-team)",
        image: {
            src: "/assets/mc_lock.jpg",
            alt: "A LITELOK D-lock secured through the spokes and brake disc of a parked motorbike's orange-rimmed rear wheel",
        },
        datePublished: "2026-06-06",
        dateModified: "2026-07-05",
    },
    {
        slug: "grand-tour-europe",
        title:
            "Four bikes, one tunnel, and the greatest roads in the Alps",
        summary:
            "A 2,000-mile ride report from June 2026: four bikes, twelve days, five countries, the great Swiss passes from Fiesch, the Lötschberg car train, the Black Forest and the long road home.",
        readingTime: "8 min read",
        icon: <TeamOutlined />,
        accentColor: "var(--gt-feature-team)",
        image: {
            src: "/assets/grand_tour_2026.jpg",
            alt: "BMW adventure motorbike with panniers and top box parked at a high Alpine pass viewpoint, snowcapped mountains under a blue sky",
        },
        datePublished: "2026-06-28",
        dateModified: "2026-07-03",
    },
    {
        slug: "fuel-prices",
        title: "Fuel prices by country — petrol & diesel",
        summary:
            "Average pump prices for unleaded 95 and diesel across the EU, UK and US. Refreshed weekly from the EU Weekly Oil Bulletin, gov.uk and the U.S. EIA.",
        readingTime: "Updated weekly",
        icon: <DollarOutlined />,
        accentColor: "var(--gt-feature-export)",
        image: {
            src: "/assets/fuel_prices.jpg",
            alt: "Fuel pump nozzle at a service-station forecourt",
        },
        datePublished: "2026-06-06",
        dateModified: "2026-06-29",
    },
    {
        slug: "motorbike-navigation-tools",
        title:
            "Motorbike navigation tools — what to actually use in 2026",
        summary:
            "Calimoto, Kurviger, komoot, Google Maps, Apple Maps, Garmin Zumo XT2, TomTom Rider, Carpuride, Chigee AIO, BMW Navigator — the honest map of what motorbike riders reach for, with the trade-offs I've hit on tour.",
        readingTime: "9 min read",
        icon: <AimOutlined />,
        accentColor: "var(--gt-feature-itinerary)",
        image: {
            src: "/assets/motorcycle_navigation_hero.jpg",
            alt: "Carpuride CarPlay unit mounted above a motorbike's TFT dashboard",
        },
        datePublished: "2026-07-04",
        dateModified: "2026-07-04",
    },
    {
        slug: "cycle-navigation-tools",
        title:
            "Cycle navigation tools — what to actually use in 2026",
        summary:
            "komoot, Ride with GPS, Strava, Bikemap, Google Maps, Garmin Edge, Wahoo Elemnt, Hammerhead Karoo, Bryton — the honest map of what cyclists reach for on a tour, with the trade-offs that matter.",
        readingTime: "8 min read",
        icon: <AimOutlined />,
        accentColor: "var(--gt-feature-expense)",
        image: {
            src: "/assets/cycle_navigation_hero.jpeg",
            alt: "Wahoo Elemnt head unit mounted on drop-bar handlebars showing a route",
        },
        datePublished: "2026-07-04",
        dateModified: "2026-07-04",
    },
    {
        slug: "motorbike-tool-kit",
        title: "The motorbike tool kit — what to carry, and what to leave",
        summary:
            "What actually rides with me — a tiny day-out kit of puncture repair, pump, cable ties, a multi-tool and straps, the bigger roll that comes out on long tours, and why breakdown cover is the most useful tool on the bike.",
        readingTime: "7 min read",
        icon: <ToolOutlined />,
        accentColor: "var(--gt-feature-export)",
        image: {
            src: "/assets/mc_toolkit.jpg",
            alt: "A motorbike tool roll laid open on a table with tools strapped in rows — Wera hex keys and a ratcheting screwdriver, locking pliers, Knipex water-pump pliers, an adjustable wrench, combination spanners, a socket ratchet and cable ties",
        },
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
    },
    {
        slug: "motorbike-puncture-repair",
        title: "How to fix a tyre puncture on a motorbike",
        summary:
            "The roadside drill for a punctured motorbike tyre: telling tubeless from tubed before it happens, the plug kit that turns a nail into a ten-minute fix, reinflating with a pump or CO2, and knowing when a puncture is a recovery job rather than a repair.",
        readingTime: "6 min read",
        icon: <ToolOutlined />,
        accentColor: "var(--gt-feature-itinerary)",
        datePublished: "2026-08-09",
    },
    {
        slug: "channel-crossings",
        title:
            "Getting your motorbike across the Channel — ferry vs Eurotunnel",
        summary:
            "LeShuttle, Dover–Calais, or an overnight boat to Holland or Spain: how each crossing works with a bike, what strapping down on the vehicle deck actually looks like, when to book, and why the long ferries are cheaper than they look once you count the hotel nights they replace.",
        readingTime: "9 min read",
        icon: <CompassOutlined />,
        accentColor: "var(--gt-feature-itinerary)",
        image: {
            // Unsplash (free licence): ferry off the White Cliffs of Dover,
            // photo by Min(Felix) Xu — unsplash.com/photos/abhfAckCybQ
            src: "/assets/channel_ferry.jpg",
            alt: "Cross-channel ferry passing the White Cliffs of Dover",
        },
        datePublished: "2026-08-29",
    },
    {
        slug: "europe-trip-cost",
        title:
            "What will a Europe motorbike trip cost? An interactive calculator",
        summary:
            "Set your days, accommodation, distance, meals and Channel crossing for a live estimate of what a European motorbike tour costs — built on average prices for each choice, in £ or €.",
        readingTime: "Interactive",
        icon: <CalculatorOutlined />,
        accentColor: "var(--gt-feature-currency)",
        datePublished: "2026-07-15",
        dateModified: "2026-07-15",
    },
];
